import { useCallback, useState } from 'react';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useMutation } from 'data-fetching-client';
import { View } from 'foundations-components/components/View';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { Section } from 'foundations-components/transitional/components/Section';
import HelpfulButton from 'marketplace-ui-reviews/components/HelpfulButton';
import Rating from 'marketplace-ui-reviews/components/Rating';
import ReportButton from 'marketplace-ui-reviews/components/ReportButton';
import ReviewActionsWrapper from 'marketplace-ui-reviews/components/ReviewActionsWrapper';
import ReviewBody from 'marketplace-ui-reviews/components/ReviewBody';
import ReviewEditToggle from 'marketplace-ui-reviews/components/ReviewEditToggle';
import ReviewReplyThread from 'marketplace-ui-reviews/components/ReviewReplyThread';
import ReviewTranslationToggle from 'marketplace-ui-reviews/components/ReviewTranslationToggle';
import ReviewerInfo from 'marketplace-ui-reviews/components/ReviewerInfo';
import ReportReviewModal from 'marketplace-ui-reviews/components/modals/ReportReviewModal';
import { RatingAxes } from 'marketplace-ui-reviews/constants/RatingAxes';
import { REPORT_REVIEW } from 'marketplace-ui-reviews/queries/reportReviewQuery';
import { SET_REVIEW_HELPFUL } from 'marketplace-ui-reviews/queries/setReviewHelpfulQuery';
import { getUserMarkedHelpful } from 'marketplace-ui-reviews/utils/getUserMarkedHelpful';
import { useDetailsPageContext } from '../context/DetailsPageContext';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export default function ReviewRenderer({
  borderBottom,
  review,
  searchTerm,
  reviewType,
  onTrackInteraction,
  userId
}) {
  const {
    appContext
  } = useDetailsPageContext();
  const isInApp = appContext === 'app';
  const helpfulCount = review.getHelpfulCount();
  const userMarkedHelpful = getUserMarkedHelpful(userId.toString(), review);
  const [setIsHelpful] = useMutation(SET_REVIEW_HELPFUL);
  const [reportReview] = useMutation(REPORT_REVIEW);
  const [showOriginal, setShowOriginal] = useState(false);
  const [showUntranslatedText, setShowUntranslatedText] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [userMarkedHelpfulState, setUserMarkedHelpfulState] = useState(userMarkedHelpful);
  const [helpfulCountState, setHelpfulCountState] = useState(helpfulCount);
  const showReviewEditToggle = review.get('lastEditedAt') && review.get('original').ratings.get('OVERALL') !== review.get('ratings').get('OVERALL');
  const ratingValue = showOriginal ? review.get('original').ratings.get(RatingAxes.OVERALL) : review.ratings.get(RatingAxes.OVERALL);
  const onReviewModalConfirm = useCallback(({
    reason,
    comment
  }) => {
    return new Promise((resolve, reject) => {
      reportReview({
        variables: {
          reviewId: review.get('id'),
          reportReason: reason,
          comment
        }
      }).then(() => {
        setShowReportModal(false);
        resolve();
      }).catch(err => {
        reject(err);
      });
    });
  }, [reportReview, review]);
  const onClickHelpful = useCallback(() => {
    return new Promise((resolve, reject) => {
      setIsHelpful({
        variables: {
          reviewId: review.get('id'),
          isHelpful: !userMarkedHelpfulState
        }
      }).then(() => {
        setUserMarkedHelpfulState(!userMarkedHelpfulState);
        if (userMarkedHelpfulState) {
          setHelpfulCountState(helpfulCountState - 1);
        } else {
          setHelpfulCountState(helpfulCountState + 1);
        }
        resolve();
      }).catch(err => {
        reject(err);
      });
    });
  }, [review, userMarkedHelpfulState, setIsHelpful, helpfulCountState]);
  return /*#__PURE__*/_jsxs(View, {
    as: "div",
    inlineSize: "100%",
    paddingBlock: "800",
    borderBlockEndStyle: borderBottom ? 'solid' : undefined,
    borderBlockEndWidth: borderBottom ? '100' : undefined,
    borderBlockEndColor: borderBottom ? 'border-core-subtle' : undefined,
    children: [/*#__PURE__*/_jsx(Section, {
      children: /*#__PURE__*/_jsx(Rating, {
        direction: "column",
        label: /*#__PURE__*/_jsxs(List, {
          inline: true,
          use: "inline-divided",
          children: [/*#__PURE__*/_jsx(FormattedDateTime, {
            value: review.get('createdAt'),
            format: "ll",
            type: "userTz"
          }), review.get('lastEditedAt') && /*#__PURE__*/_jsxs(View, {
            as: "span",
            marginInlineStart: "100",
            children: [/*#__PURE__*/_jsx(View, {
              as: "span",
              marginInlineEnd: "100",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "marketplace-ui-reviews.edit.edited"
              })
            }), /*#__PURE__*/_jsx(FormattedDateTime, {
              value: review.get('lastEditedAt'),
              format: "ll",
              type: "userTz"
            })]
          })]
        }),
        rating: ratingValue,
        showTextRating: true
      })
    }), /*#__PURE__*/_jsx(ReviewBody, {
      review: review,
      searchTerm: searchTerm,
      showOriginal: showOriginal,
      showUntranslatedText: showUntranslatedText,
      reviewType: reviewType,
      onTrackInteraction: onTrackInteraction
    }), /*#__PURE__*/_jsx(ReviewerInfo, {
      review: review,
      inline: true
    }), /*#__PURE__*/_jsxs(ReviewActionsWrapper, {
      itemRight: /*#__PURE__*/_jsx(ReviewTranslationToggle, {
        review: review,
        onTrackInteraction: onTrackInteraction,
        showUntranslatedText: showUntranslatedText,
        setShowUntranslatedText: setShowUntranslatedText
      }),
      children: [/*#__PURE__*/_jsx(ListItem, {
        children: /*#__PURE__*/_jsx(HelpfulButton, {
          disabled: !isInApp,
          helpfulCount: helpfulCountState,
          onClickHelpful: onClickHelpful,
          userMarkedHelpful: userMarkedHelpfulState
        })
      }), /*#__PURE__*/_jsx(ListItem, {
        children: /*#__PURE__*/_jsx(ReportButton, {
          isCustomer: isInApp,
          onClick: () => setShowReportModal(true)
        })
      }), showReviewEditToggle && /*#__PURE__*/_jsx(ListItem, {
        children: /*#__PURE__*/_jsx(ReviewEditToggle, {
          review: review,
          showOriginal: showOriginal,
          setShowOriginal: setShowOriginal
        })
      })]
    }), /*#__PURE__*/_jsx(ReviewReplyThread, {
      reply: review.getReply(showUntranslatedText)
    }), showReportModal && /*#__PURE__*/_jsx(ReportReviewModal, {
      onClose: () => setShowReportModal(false),
      onConfirm: onReviewModalConfirm,
      review: review,
      type: reviewType
    })]
  });
}